import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Linkedin, Target, Eye, Users } from 'lucide-react';
import { SEO } from '@/components/SEO';

const TEAM = [
  {
    name: 'Uday Disley',
    role: 'Co-Founder',
    image: '/images/uday-disley.jpg',
    bio: 'Previously built a successful EV fintech platform. Brings deep expertise in energy and financial technology.',
  },
  {
    name: 'Sachin Mehta',
    role: 'Co-Founder',
    image: '/images/sachin-mehta.jpg',
    bio: 'Co-founded the EV fintech platform with Uday. Combines innovation with hands-on execution in energy storage.',
  },
];

const VALUES = [
  { icon: Target, title: 'Practical Solutions', desc: 'We focus on dependable storage systems with lower costs and sustainable delivery models.' },
  { icon: Eye, title: 'Innovation', desc: 'Combining digital-first energy management with scalable physical infrastructure.' },
  { icon: Users, title: 'Hands-on Execution', desc: 'From assessment to deployment, our team is involved at every step of the process.' },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Us"
        path="/about"
        description="Meet the Voltide team. We're building India's clean energy future with modular battery storage solutions."
      />
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="voltide-container">
          <div className="max-w-3xl animate-fade-up">
            <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-blue-50 text-[#0061DD] border-blue-100 mb-6">
              About Us
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-6">
              Building the future of <span className="blue-gradient-text">clean energy</span>
            </h1>
            <p className="text-base lg:text-lg text-[#57534E] leading-relaxed max-w-2xl">
              Voltide is an energy storage company focused on practical, scalable BESS for commercial, industrial and residential communities. Our goal is dependable storage systems with lower costs and sustainable delivery models.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="pb-20 lg:pb-24" data-testid="about-mission">
        <div className="voltide-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img src="/images/voltide-solar-ev.png" alt="Voltide Solar + BESS" className="sketch-img rounded-2xl w-full" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-6">Our Mission</h2>
              <p className="text-[#57534E] leading-relaxed mb-6">
                India's energy landscape is at an inflection point. Diesel generators — noisy, polluting, and expensive — still power millions of commercial and residential establishments as backup. We're changing that.
              </p>
              <p className="text-[#57534E] leading-relaxed mb-6">
                Voltide provides affordable, long-term BESS rental solutions tailored to your energy needs. Our OPEX-first model means businesses can switch to clean energy storage without the burden of upfront capital expenditure.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                {VALUES.map((v, i) => (
                  <div key={v.title} className="text-center" data-testid={`value-${i}`}>
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-3">
                      <v.icon className="w-6 h-6 text-[#0061DD]" />
                    </div>
                    <h3 className="text-sm font-bold text-[#1A1A1A] mb-1">{v.title}</h3>
                    <p className="text-xs text-[#57534E]">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 lg:py-24 bg-white" data-testid="team-section">
        <div className="voltide-container">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1A1A]">
              Meet the Founders
            </h2>
            <p className="mt-3 text-base text-[#57534E]">
              Cofounded by experienced entrepreneurs from the EV fintech space
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {TEAM.map((member, i) => (
              <div key={member.name} className="bento-card group" data-testid={`team-member-${i}`}>
                <div className="aspect-[3/4] overflow-hidden bg-[#F9F9F7]">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1A1A1A]">{member.name}</h3>
                  <p className="text-sm font-medium text-[#0061DD] mb-3">{member.role}</p>
                  <p className="text-sm text-[#57534E] leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20 lg:py-24" data-testid="company-info">
        <div className="voltide-container">
          <div className="bento-card p-8 lg:p-12 max-w-2xl mx-auto text-center">
            <img src="/images/voltide-logo.svg" alt="Voltide" className="h-10 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-[#1A1A1A] mb-4">Voltide Solutions Private Limited</h2>
            <div className="space-y-2 text-sm text-[#57534E]">
              <p>c/o WeWork Latitude, Bellary Road</p>
              <p>Bengaluru, India 560024</p>
              <p className="text-[#0061DD] font-medium">info@voltide.energy</p>
              <p className="text-xs text-[#A8A29E] mt-4">CIN: U38300KA2024PTC193948</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-[#0061DD]">
        <div className="voltide-container text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Let's build a cleaner future together
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto">
            Partner with Voltide for reliable, affordable battery energy storage.
          </p>
          <Link to="/contact">
            <Button className="rounded-full bg-white text-[#0061DD] hover:bg-blue-50 px-8 py-6 text-lg font-semibold gap-2" data-testid="about-cta-btn">
              Get in Touch <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
